import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PROJECTS, CATEGORIES } from "@/data/content";
import { Reveal, LineReveal } from "./motion/Reveal";
import { scrollToId } from "@/lib/scroll";

const ProjectCard = ({ p, i, onOpen }) => (
  <motion.article
    layout
    initial={{ opacity: 0, y: 40 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, scale: 0.97 }}
    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.08 }}
    className={`group ${i % 3 === 1 ? "md:mt-16" : ""}`}
  >
    <button data-testid={`portfolio-card-${p.id}`} data-cursor="View" onClick={() => onOpen(p)} className="block w-full text-left">
      <div className="relative overflow-hidden aspect-[4/3] bg-sand border border-taupe-line">
        <img src={p.image} alt={`${p.name} website concept mockup`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.06]" />
        <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/20 transition-colors duration-700" />
        <span className="absolute top-4 left-4 glass border border-taupe-line px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em] text-charcoal">{p.year}</span>
        <span className="absolute bottom-4 right-4 translate-y-3 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-[opacity,transform] duration-500 bg-ivory text-charcoal text-xs px-4 py-2 rounded-full flex items-center gap-2">View Project <ArrowUpRight size={14} /></span>
      </div>
      <div className="flex items-start justify-between gap-4 mt-5">
        <div>
          <h3 className="font-serif text-2xl sm:text-3xl font-light text-charcoal leading-tight">{p.name}</h3>
          <p className="text-sm text-ink/65 font-light mt-2 max-w-sm leading-relaxed">{p.desc}</p>
        </div>
        <span className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-sage mt-2">{p.category}</span>
      </div>
    </button>
  </motion.article>
);

export const Work = () => {
  const [cat, setCat] = useState("All");
  const [open, setOpen] = useState(null);
  const list = cat === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === cat);

  return (
    <section id="work" data-testid="work-section" className="section-y bg-ivory">
      <div className="container-x">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12 lg:mb-16">
          <div className="lg:col-span-7">
            <Reveal><p className="eyebrow mb-6">Our Work</p></Reveal>
            <LineReveal inView as="h2" lines={["Concepts that show", <>how we <span className="italic text-sage">think.</span></>]} className="h2" />
          </div>
          <Reveal delay={0.2} className="lg:col-span-5 body-lg">
            A selection of portfolio concept studies across industries, created to demonstrate the level of design, interaction and detail we bring to every project. Your business could be next.
          </Reveal>
        </div>

        <Reveal className="flex flex-wrap gap-2 mb-12 lg:mb-16 border-y border-taupe-line py-4" role="tablist" aria-label="Filter projects by category">
          {CATEGORIES.map((c) => (
            <button key={c} role="tab" aria-selected={cat === c} data-testid={`portfolio-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCat(c)} className={`px-4 py-2 rounded-full text-xs tracking-wide transition-colors duration-300 ${cat === c ? "bg-charcoal text-ivory" : "text-ink/70 hover:bg-cream"}`}>
              {c}
            </button>
          ))}
        </Reveal>

        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 lg:gap-x-12">
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => <ProjectCard key={p.id} p={p} i={i} onOpen={setOpen} />)}
          </AnimatePresence>
        </motion.div>
      </div>

      <Dialog open={!!open} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="portfolio-modal" className="max-w-4xl p-0 bg-ivory border-taupe-line overflow-hidden rounded-none gap-0 max-h-[90vh] overflow-y-auto" data-lenis-prevent>
          {open && (
            <div className="grid grid-cols-1 md:grid-cols-12">
              <div className="md:col-span-7 aspect-[4/3] md:aspect-auto bg-sand"><img src={open.image} alt={`${open.name} website concept`} className="h-full w-full object-cover" /></div>
              <div className="md:col-span-5 p-8 lg:p-10 flex flex-col">
                <p className="eyebrow">{open.category} · {open.year}</p>
                <DialogTitle className="font-serif text-4xl font-light text-charcoal mt-5 leading-tight">{open.name}</DialogTitle>
                <DialogDescription className="body-lg mt-5 !text-ink/75">{open.detail}</DialogDescription>
                <p className="text-xs text-taupe mt-6 font-light">This is a portfolio concept study created by Amara Studios to demonstrate design direction, not a live client website.</p>
                <button data-testid="portfolio-modal-enquire" onClick={() => { setOpen(null); setTimeout(() => scrollToId("contact"), 200); }} className="btn btn-dark mt-auto pt-3.5 self-start mt-8"><span>Get a Quote</span></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};
